// components/video/VideoCardPlayer.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import Hls from "hls.js";

/** ---- global audio bus: only one video has sound ---- */
const getAudioBus = () => {
  if (typeof window === "undefined") return null;
  if (!window.__YZY_AUDIO_BUS__) window.__YZY_AUDIO_BUS__ = new EventTarget();
  return window.__YZY_AUDIO_BUS__;
};
/** ---- global play bus: only one video plays ---- */
const getPlayBus = () => {
  if (typeof window === "undefined") return null;
  if (!window.__YZY_PLAY_BUS__) window.__YZY_PLAY_BUS__ = new EventTarget();
  return window.__YZY_PLAY_BUS__;
};
/** ---- safe CSS injector ---- */
function ensureStyle(id, css) {
  if (typeof document === "undefined") return;
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("style");
    tag.id = id;
    document.head.appendChild(tag);
  }
  if (!tag.textContent.includes(css.trim())) tag.textContent += css;
}
const isiOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

export default function VideoCardPlayer({
  src,                 // HLS .m3u8 URL
  poster,
  autoPlay = true,
  loop = true,
  muted = true,        // keep true for iOS autoplay
  controls = false,    // ignored; custom UI
  fit = "cover",
  visibilityThreshold = 0.6,
}) {
  const idRef = useRef(Math.random().toString(36).slice(2));
  const videoRef = useRef(null);
  const wrapRef  = useRef(null);
  const hlsRef   = useRef(null);

  const [ready, setReady] = useState(false);
  const [inView, _setInView] = useState(false);
  const inViewRef = useRef(false);
  const setInView = (v) => { inViewRef.current = v; _setInView(v); };

  const [playing, setPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(!!muted);
  const [volume, setVolume] = useState(1);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [buffered, setBuffered] = useState(0);

  // spinner: only show when truly stalled
  const [isBuffering, setIsBuffering] = useState(false);
  const lastTimeUpdateRef = useRef(0);
  const waitingTimerRef = useRef(null);

  const [isScrubbing, setIsScrubbing] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(1);
  const speeds = [0.5, 1.0, 1.25, 1.5, 2.0];

  // DO NOT encode the whole URL on iOS; Safari is fine with raw URL.
  const safeSrc = useMemo(() => (src || ""), [src]);
  const audioBus = getAudioBus();
  const playBus  = getPlayBus();

  /* ---- INIT: attach HLS (recreate only if URL/loop changes) ---- */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !safeSrc) return;

    // cleanup any previous instance
    try { hlsRef.current?.destroy(); } catch {}
    hlsRef.current = null;

    // base attrs
    v.loop = loop;
    v.playsInline = true;
    v.preload = "metadata";
    v.muted = isMuted; // initial state (attributes also set on element)
    v.volume = volume;
    setReady(false);

    const showSpinnerIfTrulyStuck = () => {
      clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = setTimeout(() => {
        const now = performance.now();
        const since = now - (lastTimeUpdateRef.current || 0);
        if (!v.paused && since > 400) setIsBuffering(true);
      }, 250);
    };

    const logMediaError = (label) => {
      const err = v.error;
      if (err) console.warn(`[Video] ${label}`, err, err?.message, err?.code);
    };

    const onLoadStart = () => { if (autoPlay && inViewRef.current && v.muted) showSpinnerIfTrulyStuck(); };
    const onLoaded = () => { setDuration(v.duration || 0); setReady(true); };
    const onTime = () => {
      lastTimeUpdateRef.current = performance.now();
      setTime(v.currentTime || 0);
      if (isBuffering && v.readyState >= 3) setIsBuffering(false);
    };
    const onProgress = () => {
      try {
        if (v.buffered.length > 0 && v.duration > 0) {
          const end = v.buffered.end(v.buffered.length - 1);
          setBuffered(Math.max(0, Math.min(1, end / v.duration)));
        }
      } catch {}
    };
    const onPlaying = () => { setIsBuffering(false); setPlaying(true); };
    const onPause   = () => { setPlaying(false); setIsBuffering(false); };
    const onWaiting = () => { if (!v.paused) showSpinnerIfTrulyStuck(); };
    const onStalled = () => { if (!v.paused) showSpinnerIfTrulyStuck(); };
    const onSeeking = () => { if (!v.paused) showSpinnerIfTrulyStuck(); };
    const onCanPlay = () => setIsBuffering(false);
    const onEmptied = () => { if (!v.paused) showSpinnerIfTrulyStuck(); };
    const onError   = () => logMediaError("HTMLMediaElement error");

    v.addEventListener("loadstart", onLoadStart);
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("progress", onProgress);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("emptied", onEmptied);
    v.addEventListener("error", onError);

    // iOS fullscreen passive hooks
    const onBeginFS = () => {};
    const onEndFS   = () => {};
    v.addEventListener("webkitbeginfullscreen", onBeginFS);
    v.addEventListener("webkitendfullscreen", onEndFS);

    // HLS hookup: Safari/iOS uses native HLS, others use hls.js
    if (v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = safeSrc;
      v.load();
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        capLevelToPlayerSize: true,
        startLevel: -1,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        fragLoadingRetryDelay: 500,
        manifestLoadingRetryDelay: 800,
      });
      hlsRef.current = hls;
      hls.attachMedia(v);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(safeSrc));
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        console.warn("[hls.js] error", data);
        if (data?.type === Hls.ErrorTypes.NETWORK_ERROR && !data?.fatal) {
          showSpinnerIfTrulyStuck();
          hls.startLoad();
        } else if (data?.fatal) {
          try { hls.destroy(); } catch {}
        }
      });
    } else {
      console.warn("HLS not supported in this browser.");
    }

    // Autoplay only when muted (iOS rule)
    if (autoPlay && inViewRef.current && v.muted) {
      showSpinnerIfTrulyStuck();
      v.play().catch((e) => {
        console.warn("Autoplay blocked", e);
        setIsBuffering(false);
      });
    }

    return () => {
      clearTimeout(waitingTimerRef.current);
      v.removeEventListener("loadstart", onLoadStart);
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("progress", onProgress);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("emptied", onEmptied);
      v.removeEventListener("error", onError);
      v.removeEventListener("webkitbeginfullscreen", onBeginFS);
      v.removeEventListener("webkitendfullscreen", onEndFS);
      try { hlsRef.current?.destroy(); } catch {}
      hlsRef.current = null;
      v.removeAttribute("src");
      try { v.load(); } catch {}
    };
  }, [safeSrc, loop]); // do NOT include isMuted/volume/autoPlay

  /* ---- apply mute/volume without rebuilding player ---- */
  useEffect(() => {
    const v = videoRef.current; if (v) v.muted = isMuted;
  }, [isMuted]);
  useEffect(() => {
    const v = videoRef.current; if (v) v.volume = volume;
  }, [volume]);

  /* ---- intersection observer ---- */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const ENTER = Math.min(1, visibilityThreshold + 0.10);
    const EXIT  = Math.max(0, visibilityThreshold - 0.15);
    const TOP_MARGIN = 64;
    const BOTTOM_MARGIN = 100;

    let debounceTimer = null;

    const io = new IntersectionObserver(
      (entries) => {
        const ratio = Math.max(...entries.map(e => e.intersectionRatio || 0));
        const cur = inViewRef.current;
        let next = cur;

        if (!cur && ratio >= ENTER) next = true;
        else if (cur && ratio <= EXIT) next = false;

        if (next !== cur) {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => setInView(next), 150);
        }
      },
      { root: null, threshold: [visibilityThreshold], rootMargin: `-${TOP_MARGIN}px 0px -${BOTTOM_MARGIN}px 0px` }
    );

    io.observe(el);
    return () => { if (debounceTimer) clearTimeout(debounceTimer); io.disconnect(); };
  }, [visibilityThreshold]);

  /* ---- react to visibility changes ---- */
  useEffect(() => {
    const v = videoRef.current; if (!v || !ready) return;
    if (inView) {
      if (autoPlay && v.muted) {
        playBus?.dispatchEvent(new CustomEvent("request-play", { detail: { id: idRef.current } }));
        v.play().catch(() => {});
      }
    } else {
      v.pause();
      setIsBuffering(false);
    }
  }, [inView, ready, autoPlay, playBus]);

  /* ---- only one video can have sound ---- */
  useEffect(() => {
    const onRequestAudio = (e) => {
      const { id } = (e && e.detail) || {};
      if (id !== idRef.current) softMute();
    };
    audioBus?.addEventListener("request-audio", onRequestAudio);
    return () => audioBus?.removeEventListener("request-audio", onRequestAudio);
  }, [audioBus]);

  /* ---- only one video can be playing ---- */
  useEffect(() => {
    const onRequestPlay = (e) => {
      const { id } = (e && e.detail) || {};
      if (id !== idRef.current) {
        const v = videoRef.current;
        if (v && !v.paused) v.pause();
      }
    };
    playBus?.addEventListener("request-play", onRequestPlay);
    return () => playBus?.removeEventListener("request-play", onRequestPlay);
  }, [playBus]);

  /* ---- keep playbackRate in sync ---- */
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = speeds[speedIdx];
  }, [speedIdx]);

  /* ---- helpers ---- */
  // Format as M:SS under an hour, otherwise H:MM:SS
  const fmt = (s, forceHours = false) => {
    if (!Number.isFinite(s)) return forceHours ? "0:00:00" : "0:00";
    const total = Math.max(0, Math.floor(s));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    if (forceHours || h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    }
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const togglePlay = async () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) {
      playBus?.dispatchEvent(new CustomEvent("request-play", { detail: { id: idRef.current } }));
      try { await v.play(); setPlaying(true); } catch (e) {
        console.warn("play() blocked", e);
      }
    } else {
      v.pause(); setPlaying(false); setIsBuffering(false);
    }
  };

  const requestSoloAudio = () => {
    audioBus?.dispatchEvent(new CustomEvent("request-audio", { detail: { id: idRef.current } }));
  };
  const softMute = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = true; setIsMuted(true);
  };
  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    const next = !isMuted;
    setIsMuted(next);
    if (!next) {
      requestSoloAudio();
      if (v.volume === 0) { v.volume = 0.5; setVolume(0.5); }
    }
  };

  const onSeek = (e) => {
    const v = videoRef.current; if (!v) return;
    const val = Number(e.target.value);
    v.currentTime = val * duration; setTime(v.currentTime);
  };

  const onScrubStart = () => { setIsScrubbing(true); };
  const onScrubEnd = () => { setIsScrubbing(false); };

  const toggleFullscreen = () => {
    const el = wrapRef.current;
    const v  = videoRef.current;
    if (!v || !el) return;

    if (isiOS() && v.webkitEnterFullscreen) {
      try { v.webkitEnterFullscreen(); } catch {}
      return;
    }
    const isFS = document.fullscreenElement || document.webkitFullscreenElement;
    if (isFS) {
      (document.exitFullscreen?.() || document.webkitExitFullscreen?.()).catch?.(()=>{});
    } else {
      (el.requestFullscreen?.() || el.webkitRequestFullscreen?.()).call(el).catch?.(()=>{});
    }
  };

  const cycleSpeed = () => { setSpeedIdx((i) => (i + 1) % speeds.length); };

  /* ---- render ---- */
  const playedPct = duration ? (time / duration) * 100 : 0;
  const buffPct   = (buffered || 0) * 100;
  const showHrs   = (duration ?? 0) >= 3600 || (time ?? 0) >= 3600;

  return (
    <div
      ref={wrapRef}
      className="twv-wrap"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: "#000",
        borderRadius: 12,
        overflow: "hidden",
        userSelect: "none",
        transform: "translateZ(0)"
      }}
    >
      <video
        ref={videoRef}
        key={safeSrc}            // force Safari to fully reload on src change
        poster={poster}
        // IMPORTANT for iOS: present at first paint
        muted
        autoPlay
        playsInline
        preload="metadata"
        // crossOrigin="anonymous" // only if your HLS + segments send ACAO headers
        style={{
          width: "100%",
          height: "100%",
          objectFit: fit,
          display: "block",
          background: "#000"
        }}
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
      />

      {/* Big center play button */}
      {!playing && ready && (
        <button
          type="button"
          onClick={togglePlay}
          className="twv-center-btn"
          aria-label="Play"
          style={centerBtnStyle}
        >
          <PlayIcon size={28} />
        </button>
      )}

      {/* Spinner only when truly stuck */}
      {isBuffering && (
        <div style={spinnerWrapStyle} aria-hidden="true">
          <div className="twv-spinner" style={spinnerStyle} />
        </div>
      )}

      {/* Controls */}
      <div className="twv-controls" style={controlsStyle}>
        <input
          type="range"
          min="0"
          max="1"
          step="0.001"
          value={duration ? time / duration : 0}
          onChange={onSeek}
          onMouseDown={onScrubStart}
          onMouseUp={onScrubEnd}
          onTouchStart={onScrubStart}
          onTouchEnd={onScrubEnd}
          aria-label="Seek"
          style={{
            ...seekStyle,
            background: `linear-gradient(
              to right,
              #fff ${playedPct}%,
              #888 ${playedPct}%,
              #888 ${buffPct}%,
              rgba(255,255,255,.35) ${buffPct}%
            )`
          }}
        />

        <div style={barRowStyle}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} style={iconBtnStyle} title={playing ? "Pause" : "Play"}>
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>

            {/* 🔊 Only mute/unmute — no volume slider */}
            <button onClick={toggleMute} aria-label={isMuted ? "Unmute" : "Mute"} title={isMuted ? "Unmute" : "Mute"} style={iconBtnStyle}>
              <VolumeIcon muted={isMuted || volume === 0} level={volume} />
            </button>

            <span style={{ fontSize: 12, opacity: 0.9, minWidth: showHrs ? 96 : 76, textAlign: "right" }}>
              {fmt(time, showHrs)} / {fmt(duration, showHrs)}
            </span>

            <button onClick={cycleSpeed} aria-label="Playback speed" title="Playback speed" style={iconBtnStyle}>
              {speeds[speedIdx]}×
            </button>
          </div>

          <div style={{ flex: 1 }} />
          <button onClick={toggleFullscreen} aria-label="Fullscreen" style={iconBtnStyle} title="Fullscreen">
            <FullscreenIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- icons ---- */
function PlayIcon({ size = 18 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>;
}
function PauseIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>;
}
function FullscreenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 3H3v5M21 8V3h-5M3 16v5h5M16 21h5v-5" />
    </svg>
  );
}
function VolumeIcon({ muted, level = 1 }) {
  if (muted || level === 0) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" role="img" aria-hidden="true"
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
        <path d="M11 5v14l-5-4H2V9h4l5-4z" />
        <line x1="19" y1="9" x2="23" y2="13" />
        <line x1="23" y1="9" x2="19" y2="13" />
      </svg>
    );
  }
  const a = level < 0.34, b = level < 0.67;
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" role="img" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
      <path d="M11 5v14l-5-4H2V9h4l5-4z" />
      {!a && <path d="M15 10a3 3 0 0 1 0 4" />}
      {!b && <path d="M17.5 7.5a6.5 6.5 0 0 1 0 9" />}
      {a && <path d="M13 12h.01" />}
    </svg>
  );
}

/* ---- styles ---- */
const controlsStyle = {
  position: "absolute", left: 0, right: 0, bottom: 0,
  padding: "8px 10px 10px",
  background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.55) 60%, rgba(0,0,0,.7) 100%)",
  color: "#fff", willChange: "transform"
};
const seekStyle = { width: "100%", appearance: "none", height: 4, borderRadius: 999, outline: "none", marginBottom: 6 };
Object.assign(seekStyle, { WebkitAppearance: "none" });

const seekThumbCss = `
.twv-wrap input[type="range"]::-webkit-slider-thumb{
  -webkit-appearance:none; appearance:none; width:12px;height:12px;border-radius:50%;
  background:#fff;border:0;box-shadow:0 0 0 2px rgba(0,0,0,.2); margin-top:-4px;
}
.twv-wrap input[type="range"]::-moz-range-thumb{ width:12px;height:12px;border-radius:50%; background:#fff;border:0;box-shadow:0 0 0 2px rgba(0,0,0,.2); }
.twv-wrap input[type="range"]::-ms-thumb{ width:12px;height:12px;border-radius:50%; background:#fff;border:0;box-shadow:0 0 0 2px rgba(0,0,0,.2); }
`;
const barRowStyle = { display: "flex", alignItems: "center", justifyContent: "space-between" };
const iconBtnStyle = { background: "transparent", color: "#fff", border: "none", borderRadius: 8, padding: 6, fontSize: 14, cursor: "pointer", lineHeight: 0, display: "grid", placeItems: "center" };

const spinnerWrapStyle = { position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none", zIndex: 4 };
const spinnerStyle = {
  width: 36, height: 36, borderRadius: "50%",
  border: "3px solid rgba(255,255,255,.25)", borderTopColor: "#fff",
  animation: "twv-spin 0.8s linear infinite", willChange: "transform",
  filter: "drop-shadow(0 0 6px rgba(0,0,0,.35))",
};
const spinnerCss = `@keyframes twv-spin { to { transform: rotate(360deg); } }`;
const centerBtnStyle = {
  position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
  width: 64, height: 64, borderRadius: "50%", background: "rgba(0,0,0,.6)", color: "#fff",
  border: "none", fontSize: 28, textAlign: "center", cursor: "pointer", backdropFilter: "blur(3px)", zIndex: 3,
};

/* inject minimal CSS once */
ensureStyle("twv-shared-css", seekThumbCss);
ensureStyle("twv-shared-css", spinnerCss);
